import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Switch,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { hedgesAdminApi, type HedgeSettings } from "@/services/api";
import {
  ArrowLeft,
  Loader2,
  Save,
  Shield,
  Percent,
  DollarSign,
  Calendar,
  CheckCircle,
  Settings,
} from "lucide-react";

export function HedgeSettingsPage() {
  const navigate = useNavigate();
  const [, setSettings] = useState<HedgeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Form state
  const [guaranteePercentage, setGuaranteePercentage] = useState("");
  // Taxa de Proteção (novo modelo)
  const [operationFeePercentage, setOperationFeePercentage] = useState("");
  const [minOperationFeeBrl, setMinOperationFeeBrl] = useState("");
  const [capitalCostPercentage, setCapitalCostPercentage] = useState("");
  // Outros limites
  const [minHedgeAmountUsdc, setMinHedgeAmountUsdc] = useState("");
  const [maxHedgeDurationDays, setMaxHedgeDurationDays] = useState("");
  const [gracePeriodDays, setGracePeriodDays] = useState("");
  const [requireApproval, setRequireApproval] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      setLoading(true);
      const data = await hedgesAdminApi.getSettings();
      setSettings(data);

      // Populate form
      setGuaranteePercentage(data.guaranteePercentage || "20");
      // Taxa de Proteção (novo modelo)
      setOperationFeePercentage(data.operationFeePercentage || "0.0030");
      setMinOperationFeeBrl(data.minOperationFeeBrl || "50");
      setCapitalCostPercentage(data.capitalCostPercentage || "0.0600");
      // Outros limites
      setMinHedgeAmountUsdc(data.minHedgeAmountUsdc || "1000");
      setMaxHedgeDurationDays(data.maxHedgeDurationDays?.toString() || "");
      setGracePeriodDays(data.gracePeriodDays?.toString() || "5");
      setRequireApproval(data.requireApproval);
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    try {
      setSaving(true);
      setSaved(false);

      await hedgesAdminApi.updateSettings({
        guaranteePercentage: parseFloat(guaranteePercentage),
        // Taxa de Proteção (novo modelo)
        operationFeePercentage: parseFloat(operationFeePercentage),
        minOperationFeeBrl: parseFloat(minOperationFeeBrl),
        capitalCostPercentage: parseFloat(capitalCostPercentage),
        // Outros limites
        minHedgeAmountUsdc: parseFloat(minHedgeAmountUsdc),
        maxHedgeDurationDays: maxHedgeDurationDays ? parseInt(maxHedgeDurationDays) : undefined,
        gracePeriodDays: parseInt(gracePeriodDays),
        requireApproval,
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Settings className="h-5 w-5 sm:h-6 sm:w-6" />}
        title="Configurações de Proteção Cambial"
        description="Configure os parâmetros do módulo de hedge"
        actions={
          <div className="flex gap-2">
            <Button className="bg-white/20 hover:bg-white/30 text-white border-0" onClick={() => navigate("/admin/hedges")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <Button className="bg-white/20 hover:bg-white/30 text-white border-0" onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : saved ? (
                <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saved ? "Salvo!" : "Salvar Alterações"}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Garantia */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Garantia
            </CardTitle>
            <CardDescription>
              Configure o percentual de garantia exigido para novos hedges
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="guaranteePercentage">Percentual de Garantia (%)</Label>
              <div className="relative mt-1.5">
                <Input
                  id="guaranteePercentage"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={guaranteePercentage}
                  onChange={(e) => setGuaranteePercentage(e.target.value)}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Ex: 20 = empresa precisa depositar 20% do valor em USDC como garantia
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Taxa de Proteção */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5" />
              Taxa de Proteção
            </CardTitle>
            <CardDescription>
              Configure a taxa cobrada. Taxa = Taxa fixa + Custo de capital (proporcional ao prazo)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label htmlFor="operationFeePercentage">Taxa Fixa da Operação (%)</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="operationFeePercentage"
                    type="number"
                    step="0.0001"
                    min="0"
                    max="1"
                    value={operationFeePercentage}
                    onChange={(e) => setOperationFeePercentage(e.target.value)}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Ex: 0.0030 = 0,30% do valor do hedge
                </p>
              </div>
              <div>
                <Label htmlFor="minOperationFeeBrl">Taxa Mínima (BRL)</Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">R$</span>
                  <Input
                    id="minOperationFeeBrl"
                    type="number"
                    step="0.01"
                    min="0"
                    value={minOperationFeeBrl}
                    onChange={(e) => setMinOperationFeeBrl(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label htmlFor="capitalCostPercentage">Custo de Capital (% a.a.)</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="capitalCostPercentage"
                    type="number"
                    step="0.0001"
                    min="0"
                    max="1"
                    value={capitalCostPercentage}
                    onChange={(e) => setCapitalCostPercentage(e.target.value)}
                    className="pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Ex: 0.0600 = 6% a.a. proporcional ao prazo
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Limites */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Limites
            </CardTitle>
            <CardDescription>
              Configure os valores mínimos e máximos permitidos
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="minHedgeAmountUsdc">Valor Mínimo do Hedge (USDC)</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  id="minHedgeAmountUsdc"
                  type="number"
                  step="0.01"
                  min="0"
                  value={minHedgeAmountUsdc}
                  onChange={(e) => setMinHedgeAmountUsdc(e.target.value)}
                  className="pl-8"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Valor mínimo que uma empresa pode contratar de hedge
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Prazos */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Prazos
            </CardTitle>
            <CardDescription>
              Configure os prazos máximos e período de carência
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="maxHedgeDurationDays">Prazo Máximo (dias)</Label>
              <Input
                id="maxHedgeDurationDays"
                type="number"
                min="1"
                value={maxHedgeDurationDays}
                onChange={(e) => setMaxHedgeDurationDays(e.target.value)}
                placeholder="Sem limite"
                className="mt-1.5"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Deixe vazio para não ter limite de prazo
              </p>
            </div>

            <div>
              <Label htmlFor="gracePeriodDays">Período de Carência (dias)</Label>
              <Input
                id="gracePeriodDays"
                type="number"
                min="0"
                value={gracePeriodDays}
                onChange={(e) => setGracePeriodDays(e.target.value)}
                className="mt-1.5"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Dias adicionais após o vencimento para liquidação antes de marcar como inadimplente
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Fluxo de Aprovação */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Fluxo de Aprovação
            </CardTitle>
            <CardDescription>
              Configure se novos hedges precisam de aprovação manual
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="requireApproval" className="text-base">
                  Exigir aprovação de administrador
                </Label>
                <p className="text-sm text-muted-foreground">
                  Se ativado, novos hedges precisarão ser aprovados antes de ficarem ativos.
                  Se desativado, hedges serão ativados automaticamente após o depósito da garantia.
                </p>
              </div>
              <Switch
                id="requireApproval"
                checked={requireApproval}
                onCheckedChange={setRequireApproval}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
